import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { fetchRequestDetail, patchRequestStatus } from "@/admin/requestsAdminApi";
import {
  FIELD_LABELS,
  REPLY_METHOD_LABELS,
  STATUS_LABELS,
  STATUS_OPTIONS,
  TYPE_LABELS,
  formatDate,
  humanizeField,
} from "@/admin/requestsLabels";

export default function AdminRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingStatus, setSavingStatus] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchRequestDetail(id);
      setDoc(data);
    } catch (e) {
      setError(e.message || "Failed to load request.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const onUpdateStatus = async (next) => {
    if (!doc || doc.status === next) return;
    setSavingStatus(next);
    try {
      const updated = await patchRequestStatus(id, next);
      setDoc(updated);
    } catch (e) {
      setError(e.message || "Failed to update status.");
    } finally {
      setSavingStatus("");
    }
  };

  if (loading) {
    return (
      <div data-testid="admin-request-detail-loading">
        <div className="h-6 w-24 bg-[color:var(--sas-accent-soft)] rounded animate-pulse" />
        <div className="mt-4 h-8 w-64 bg-[color:var(--sas-accent-soft)] rounded animate-pulse" />
        <div className="mt-8 h-48 w-full bg-white border border-[color:var(--sas-line)] rounded-xl animate-pulse" />
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div data-testid="admin-request-detail-error">
        <button
          type="button"
          onClick={() => navigate("/admin/requests")}
          className="inline-flex items-center gap-1.5 text-xs text-[color:var(--sas-ink-2)] hover:text-[color:var(--sas-ink)] mb-6"
        >
          <ArrowLeft size={14} /> Back to requests
        </button>
        <h1 className="sas-h2">Couldn’t load this request</h1>
        <p className="sas-body mt-3 text-[14px]">{error || "Request not found."}</p>
      </div>
    );
  }

  const fieldLabelMap = FIELD_LABELS[doc.request_type] || {};

  return (
    <div data-testid="admin-request-detail">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-xs text-[color:var(--sas-ink-2)] hover:text-[color:var(--sas-ink)] mb-6"
        data-testid="admin-request-detail-back"
      >
        <ArrowLeft size={14} /> Back
      </button>

      <div className="sas-eyebrow mb-2">
        {TYPE_LABELS[doc.request_type] || doc.category_label}
      </div>
      <h1 className="sas-h1">
        {(doc.fields && doc.fields.full_name) || "Untitled request"}
      </h1>
      <div className="mt-3 text-[12.5px] text-[color:var(--sas-ink-2)] flex items-center gap-2 flex-wrap">
        <span>Submitted {formatDate(doc.submitted_at)}</span>
        <span aria-hidden>·</span>
        <span>Language: {(doc.language || "ar").toUpperCase()}</span>
        <span aria-hidden>·</span>
        <span>Preferred reply: {REPLY_METHOD_LABELS[doc.preferred_reply] || doc.preferred_reply}</span>
      </div>

      {/* Status control */}
      <section
        className="mt-8 rounded-xl border border-[color:var(--sas-line)] bg-white p-5"
        data-testid="admin-request-status-control"
      >
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-sm font-semibold text-[color:var(--sas-ink)]">Status</h2>
            <p className="sas-body mt-1 text-[12.5px]">
              Move this request as you work on it. Changes are stored immediately.
            </p>
          </div>
          <div className="flex items-center gap-2" role="group" aria-label="Update status">
            {STATUS_OPTIONS.map((s) => {
              const isActive = doc.status === s;
              const saving = savingStatus === s;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => onUpdateStatus(s)}
                  disabled={isActive || !!savingStatus}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[12.5px] transition-colors ${
                    isActive
                      ? "border-[color:var(--sas-ink)] bg-[color:var(--sas-ink)] text-white"
                      : "border-[color:var(--sas-line)] text-[color:var(--sas-ink)] hover:border-[color:var(--sas-ink)]"
                  } ${savingStatus && !isActive ? "opacity-70" : ""}`}
                  data-testid={`admin-request-status-btn-${s}`}
                >
                  {isActive && <Check size={13} aria-hidden />}
                  {saving ? "Saving…" : STATUS_LABELS[s]}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Fields */}
      <section
        className="mt-6 rounded-xl border border-[color:var(--sas-line)] bg-white"
        data-testid="admin-request-fields"
      >
        <dl className="divide-y divide-[color:var(--sas-line)]">
          {Object.entries(doc.fields || {}).map(([key, value]) => {
            const label = fieldLabelMap[key] || key;
            const text = humanizeField(doc.request_type, key, value);
            const isLong =
              typeof value === "string" &&
              (value.length > 120 || value.includes("\n"));
            return (
              <div
                key={key}
                className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-2 md:gap-6 px-5 py-4"
                data-testid={`admin-request-field-${key}`}
              >
                <dt className="text-[12.5px] text-[color:var(--sas-ink-2)] font-semibold pt-0.5">
                  {label}
                </dt>
                <dd className="text-[14px] text-[color:var(--sas-ink)] whitespace-pre-wrap break-words">
                  {isLong ? text : (text || "—")}
                </dd>
              </div>
            );
          })}
        </dl>
      </section>

      {/* Metadata */}
      <section
        className="mt-6 rounded-xl border border-dashed border-[color:var(--sas-line)] p-5"
        data-testid="admin-request-meta"
      >
        <h3 className="text-xs font-semibold tracking-wide uppercase text-[color:var(--sas-ink-2)]">
          Metadata
        </h3>
        <dl className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-[12.5px]">
          <Meta label="ID" value={doc.id} mono />
          <Meta label="Updated" value={formatDate(doc.updated_at)} />
          <Meta label="Submitted IP" value={doc.ip_hint || "—"} mono />
          <Meta label="User agent" value={doc.user_agent_hint || "—"} />
        </dl>
      </section>

      <div className="mt-8">
        <Link
          to="/admin/requests"
          className="inline-flex items-center gap-1.5 text-xs text-[color:var(--sas-ink-2)] hover:text-[color:var(--sas-ink)]"
        >
          <ArrowLeft size={14} /> All requests
        </Link>
      </div>
    </div>
  );
}

function Meta({ label, value, mono }) {
  return (
    <div>
      <dt className="text-[color:var(--sas-ink-2)]">{label}</dt>
      <dd className={`mt-0.5 text-[color:var(--sas-ink)] ${mono ? "font-mono" : ""} break-all`}>
        {value}
      </dd>
    </div>
  );
}
